/**
 * Agent picker options, built from the live roster (GET /api/agents, seeded from
 * server/src/data/seedAgents.ts). Every page that lets someone pick an agent uses
 * these, so no page keeps its own copy of who exists.
 */

import type { Agent } from '../types/agent';

export interface AgentOption {
  id: string;
  label: string;
}

/** One option per roster agent, labelled "Name (Role)", in name order. */
export function buildAgentOptions(agents: ReadonlyArray<Pick<Agent, 'id' | 'name' | 'role'>>): AgentOption[] {
  return agents
    .map((agent) => ({ id: agent.id, label: `${agent.name} (${agent.role})` }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The options plus the value already saved, when the roster does not have it, so
 * opening an old record never silently changes who it is assigned to. With no
 * roster loaded (still loading, or it failed) the saved id is shown as it is.
 */
export function withCurrentOption(options: AgentOption[], current: string): AgentOption[] {
  if (!current || options.some((option) => option.id === current)) return options;
  const label = options.length === 0 ? current : `${current} (not on the roster)`;
  return [...options, { id: current, label }];
}
